import { Navigate, createBrowserRouter } from 'react-router-dom';
import { AppLayout } from './layouts/AppLayout';
import { RoleGuard } from './guards/RoleGuard';
import { useAuthStore } from './store/authStore';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { ChangePasswordPage } from '@/features/auth/pages/ChangePasswordPage';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';
import { CollectPage } from '@/features/collections/pages/CollectPage';
import { CollectionsPage } from '@/features/collections/pages/CollectionsPage';
import { CollectionDetailPage } from '@/features/collections/pages/CollectionDetailPage';
import { InvoicesPage } from '@/features/invoices/pages/InvoicesPage';
import { ShopsPage } from '@/features/shops/pages/ShopsPage';
import { AgentsPage } from '@/features/agents/pages/AgentsPage';
import { RoutesPage } from '@/features/routes/pages/RoutesPage';
import { BanksPage } from '@/features/banks/pages/BanksPage';
import { UsersPage } from '@/features/users/pages/UsersPage';
import { AuditLogPage } from '@/features/audit/pages/AuditLogPage';
import { ReportsPage } from '@/features/reports/pages/ReportsPage';
import { NotFoundPage } from '@/features/misc/NotFoundPage';

/**
 * Agents have no dashboard permission, so the index route sends them to the
 * screen they actually open the app for. Everyone else lands on the dashboard.
 */
function HomeRedirect(): JSX.Element {
  const user = useAuthStore((state) => state.user);
  return user?.role === 'AGENT' ? <Navigate to="/collect" replace /> : <DashboardPage />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/change-password',
    element: (
      // Guarded, but with no permission requirement — a user with a forced
      // password change must be able to reach exactly this page.
      <RoleGuard>
        <ChangePasswordPage />
      </RoleGuard>
    ),
  },
  {
    path: '/',
    element: (
      <RoleGuard>
        <AppLayout />
      </RoleGuard>
    ),
    children: [
      { index: true, element: <HomeRedirect /> },

      {
        path: 'collect',
        element: (
          <RoleGuard permissions={['collections:create']}>
            <CollectPage />
          </RoleGuard>
        ),
      },
      {
        path: 'collections',
        element: (
          <RoleGuard permissions={['collections:read']}>
            <CollectionsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'collections/:id',
        element: (
          <RoleGuard permissions={['collections:read']}>
            <CollectionDetailPage />
          </RoleGuard>
        ),
      },
      {
        path: 'invoices',
        element: (
          <RoleGuard permissions={['invoices:read']}>
            <InvoicesPage />
          </RoleGuard>
        ),
      },
      {
        path: 'shops',
        element: (
          <RoleGuard permissions={['shops:read']}>
            <ShopsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'agents',
        element: (
          <RoleGuard permissions={['agents:read']}>
            <AgentsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'routes',
        element: (
          <RoleGuard permissions={['routes:read']}>
            <RoutesPage />
          </RoleGuard>
        ),
      },
      {
        path: 'banks',
        element: (
          <RoleGuard permissions={['banks:create']}>
            <BanksPage />
          </RoleGuard>
        ),
      },
      {
        path: 'users',
        element: (
          <RoleGuard permissions={['users:read']}>
            <UsersPage />
          </RoleGuard>
        ),
      },
      {
        path: 'reports',
        element: (
          <RoleGuard permissions={['reports:read']}>
            <ReportsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'audit-log',
        element: (
          <RoleGuard permissions={['audit:read']}>
            <AuditLogPage />
          </RoleGuard>
        ),
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
